import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PartnerApplications } from "@/components/admin/PartnerApplications";
import { PartnerLeaderboard } from "@/components/admin/PartnerLeaderboard";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Users, DollarSign, Plus, Search, Copy, Loader2, Ban, CheckCircle2,
  Trash2, ShieldAlert, FileText, TrendingUp, Percent, HelpCircle, ChevronDown, Mail,
} from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function copy(text) {
  navigator.clipboard?.writeText(text);
  toast.success("Kopye!");
}

function genPassword() {
  const U = "ABCDEFGHJKLMNPQRSTUVWXYZ", L = "abcdefghijkmnpqrstuvwxyz", D = "23456789", S = "!@#$%&*?";
  const all = U + L + D + S;
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  let pwd = pick(U) + pick(L) + pick(D) + pick(S);
  for (let i = 0; i < 8; i++) pwd += pick(all);
  return pwd.split("").sort(() => Math.random() - 0.5).join("");
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono-term text-[10px] uppercase tracking-widest text-[#888] block mb-1">{label}</label>
      {children}
    </div>
  );
}

export function PartnersTab() {
  const [partners, setPartners] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [remindingStripe, setRemindingStripe] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", username: "", password: "", company: "", contract_type: "1_month", commission_pct: 50 });
  const [created, setCreated] = useState(null);
  const [newPw, setNewPw] = useState(null);

  const resetPw = async (id) => {
    if (!window.confirm("Jenere yon nouvo modpas? Ansyen an ap sispann mache.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/partners/${id}/reset-password`);
      setNewPw(data);
      toast.success("Nouvo modpas jenere");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([
        api.get(`/admin/partners${q ? `?q=${encodeURIComponent(q)}` : ""}`),
        api.get("/admin/partner-analytics"),
      ]);
      setPartners(p.data);
      setAnalytics(a.data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/partners", form);
      setCreated(data);
      toast.success("Patnè kreye!");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendInvite = async () => {
    if (!created) return;
    setBusy(true);
    try {
      await api.post(`/admin/partners/${created.partner.id}/send-invite`, { password: created.credentials.password });
      toast.success(`Email voye bay ${created.credentials.email}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const act = async (id, path, label) => {
    setBusy(true);
    try {
      await api.post(`/admin/partners/${id}/${path}`);
      toast.success(label);
      load();
      if (detail) openDetail(id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Efase patnè sa a nèt?")) return;
    try { await api.delete(`/admin/partners/${id}`); toast.success("Efase"); setDetail(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDetail = async (id) => {
    setNewPw(null);
    try { const { data } = await api.get(`/admin/partners/${id}`); setDetail(data); }
    catch (e) { toast.error("Pa ka chaje detay"); }
  };

  return (
    <div className="space-y-5" data-testid="partners-tab">
      <PartnerApplications onApproved={load} />
      <PartnerLeaderboard />
      <div className="border border-[#222] bg-[#0c0c0c] rounded-lg p-4 flex items-center gap-3" data-testid="remind-stripe-card">
        <Mail className="h-5 w-5 text-[#1C8CFF] shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-white">Raple patnè yo konekte Stripe</div>
          <div className="text-[11px] text-[#888]">Voye yon imèl + notifikasyon bay chak patnè ki poko konekte kont Stripe yo pou peman.</div>
        </div>
        <Button data-testid="remind-stripe-button" disabled={remindingStripe}
          onClick={async () => {
            setRemindingStripe(true);
            try {
              const { data } = await api.post("/admin/remind-partners-stripe");
              toast.success(`Raple voye bay ${data.reminded} patnè`);
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            finally { setRemindingStripe(false); }
          }}
          className="bg-[#1C8CFF] hover:bg-[#1677d6] text-white rounded-lg h-9 shrink-0">
          {remindingStripe ? <Loader2 className="h-4 w-4 animate-spin" /> : "Voye Raple"}
        </Button>
      </div>
      {/* In-panel guide for staff */}
      <div className="border border-[#1C8CFF]/40 bg-[#1C8CFF]/5 rounded-lg overflow-hidden" data-testid="partner-guide">
        <button onClick={() => setShowGuide((s) => !s)} className="w-full flex items-center justify-between px-3 py-2.5 text-left">
          <span className="flex items-center gap-2 text-sm font-semibold text-[#8fc4ff]"><HelpCircle className="h-4 w-4" />Gid: Kijan pou jere patnè yo</span>
          <ChevronDown className={`h-4 w-4 text-[#8fc4ff] transition-transform ${showGuide ? "rotate-180" : ""}`} />
        </button>
        {showGuide && (
          <div className="px-4 pb-4 pt-1 text-xs text-[#c9dcff] space-y-2.5 leading-relaxed">
            <p><span className="font-semibold text-white">1. Kreye yon patnè</span> — Klike bouton <span className="font-semibold">"Nouvo Patnè"</span>. Ranpli non, email, username (lèt sèlman), modpas (gwo lèt + ti lèt + senbòl + 6 karaktè), konpayi (opsyonèl), kontra (1/3/6 mwa oswa 1 ane), epi pousantaj komisyon (default 50%). Klike <span className="font-semibold">"Kreye Patnè"</span>.</p>
            <p><span className="font-semibold text-white">2. Bay patnè a enfo li</span> — Apre kreyasyon, yon fenèt montre email, username, modpas ak <span className="font-semibold">2 lyen referral</span> (chemen + subdomen). Kopye yo epi voye bay patnè a. Se ak sa l ap konekte epi fè piblisite.</p>
            <p><span className="font-semibold text-white">3. Lajan pataje otomatik</span> — Lè yon kliyan enskri atravè lyen patnè a epi peye, sistèm nan kalkile: patnè a touche pousantaj li, ou menm (CEO) ou kenbe rès la. Ou wè tout nan kat anwo yo ak nan lis patnè a.</p>
            <p><span className="font-semibold text-white">4. Jere yon patnè</span> — Klike sou non yon patnè nan lis la pou louvri detay li: renouvle/sispann/termine kontra, anrejistre yon vyolasyon, aktive/dezaktive, oswa efase.</p>
            <p className="text-[#8fb4e6]">Nòt: lyen chemen an (trzefxpro.com/r/...) mache touswit. Lyen subdomen an mande konfigirasyon DNS sou domèn ou an.</p>
          </div>
        )}
      </div>

      {/* Revenue summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          { label: "App Revenue", value: money(analytics?.app_revenue), icon: DollarSign },
          { label: "Partner Sales", value: money(analytics?.partner_gross), icon: TrendingUp },
          { label: "Commissions", value: money(analytics?.partner_commissions), icon: Percent },
          { label: "CEO Earnings", value: money(analytics?.ceo_earnings_from_partners), icon: DollarSign },
        ].map((s) => (
          <div key={s.label} className="bg-[#0F0F0F] border border-[#222] p-3" data-testid={`rev-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-1.5 text-[#888] mb-1.5"><s.icon className="h-3.5 w-3.5" /><span className="font-mono-term text-[10px] uppercase tracking-widest">{s.label}</span></div>
            <div className="font-heading text-lg font-bold">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666]" />
          <Input data-testid="partner-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chèche patnè..." className="pl-8 bg-[#0F0F0F] border-[#222] h-9 text-sm" />
        </div>
        <Button data-testid="new-partner-button" onClick={() => { setForm({ name: "", email: "", username: "", password: "", company: "", contract_type: "1_month", commission_pct: 50 }); setCreated(null); setShowCreate(true); }} className="h-9 bg-white text-black hover:bg-white/90 text-sm">
          <Plus className="h-4 w-4 mr-1" /> Nouvo Patnè
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-white" /></div>
      ) : partners.length === 0 ? (
        <div className="text-center py-8 text-[#666] text-sm">Poko gen patnè. Kreye premye a.</div>
      ) : (
        <div className="space-y-2">
          {partners.map((p) => (
            <div key={p.id} data-testid={`partner-row-${p.username}`} className="bg-[#0F0F0F] border border-[#222] p-3 flex items-center justify-between hover:border-white/40 cursor-pointer" onClick={() => openDetail(p.id)}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm truncate">{p.name}</span>
                  <span className={`font-mono-term text-[9px] uppercase px-1.5 py-0.5 rounded ${p.status === "active" ? "bg-[#22e07b]/15 text-[#22e07b]" : p.status === "suspended" ? "bg-[#ff5b5b]/15 text-[#ff5b5b]" : "bg-[#444]/40 text-[#999]"}`}>{p.status}</span>
                </div>
                <div className="text-xs text-[#888] truncate">@{p.username} · {p.customers} kliyan · {p.commission_pct}%</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-heading text-sm font-bold text-[#22e07b]">{money(p.revenue?.commission)}</div>
                <div className="font-mono-term text-[10px] text-[#666]">komisyon</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Revenue by plan */}
      {analytics?.by_plan?.length > 0 && (
        <div className="bg-[#0F0F0F] border border-[#222] p-3">
          <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#888] mb-2">Revni pa Plan</div>
          {analytics.by_plan.map((b) => (
            <div key={b.plan} className="flex justify-between text-xs py-1 border-b border-[#1a1a1a] last:border-0">
              <span className="text-[#ccc]">{b.plan}</span><span className="font-mono-term">{money(b.revenue)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#0A0A0A] border-[#222] text-white max-w-md">
          <DialogHeader><DialogTitle>{created ? "Patnè Kreye" : "Nouvo Patnè"}</DialogTitle></DialogHeader>
          {created ? (
            <div className="space-y-3 text-sm">
              <p className="text-[#22e07b]">Pataje enfòmasyon sa yo ak patnè a:</p>
              <div className="bg-[#0F0F0F] border border-[#222] p-3 space-y-2 font-mono-term text-xs">
                <div className="flex justify-between gap-2"><span className="text-[#888]">Email</span><span>{created.credentials.email}</span></div>
                <div className="flex justify-between gap-2"><span className="text-[#888]">Username</span><span>{created.credentials.username}</span></div>
                <div className="flex justify-between gap-2"><span className="text-[#888]">Password</span><span>{created.credentials.password}</span></div>
              </div>
              <div className="space-y-2">
                {[["Lyen Referral", created.referral.path_link]].map(([lbl, link]) => (
                  <div key={lbl}>
                    <div className="font-mono-term text-[10px] uppercase text-[#888]">{lbl}</div>
                    <div className="flex items-center gap-2 bg-[#0F0F0F] border border-[#222] px-2 py-1.5 rounded">
                      <span className="text-xs truncate flex-1">{link}</span>
                      <button onClick={() => copy(link)} className="text-[#888] hover:text-white"><Copy className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-[#888]">Nòt: lyen subdomen an mande konfigirasyon DNS wildcard sou domèn ou an. Lyen chemen an mache touswit.</p>
              <DialogFooter className="flex-col gap-2 sm:flex-col">
                <Button data-testid="send-partner-email" disabled={busy} onClick={sendInvite} className="w-full bg-[#1C8CFF] hover:bg-[#1C8CFF]/90">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1.5" />Voye Email bay Patnè a</>}
                </Button>
                <Button onClick={() => setShowCreate(false)} className="w-full bg-white text-black">Fini</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <Field label="Non konplè"><Input data-testid="partner-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0F0F0F] border-[#222] h-9" /></Field>
              <Field label="Email"><Input data-testid="partner-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0F0F0F] border-[#222] h-9" /></Field>
              <Field label="Username (lèt sèlman)"><Input data-testid="partner-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="bg-[#0F0F0F] border-[#222] h-9" /></Field>
              <Field label="Password">
                <div className="flex gap-2">
                  <Input data-testid="partner-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#0F0F0F] border-[#222] h-9 flex-1" />
                  <Button type="button" data-testid="generate-password" onClick={() => setForm({ ...form, password: genPassword() })} className="h-9 bg-[#1C8CFF] hover:bg-[#1C8CFF]/90 text-xs px-3 shrink-0">Generate</Button>
                </div>
              </Field>
              <Field label="Konpayi (opsyonèl)"><Input data-testid="partner-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-[#0F0F0F] border-[#222] h-9" /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Kontra">
                  <select data-testid="partner-contract" value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} className="w-full bg-[#0F0F0F] border border-[#222] h-9 rounded px-2 text-sm">
                    <option value="1_month">1 Mwa</option><option value="2_months">2 Mwa</option><option value="3_months">3 Mwa</option>
                    <option value="6_months">6 Mwa</option><option value="1_year">1 Ane</option>
                  </select>
                </Field>
                <Field label="Komisyon %"><Input data-testid="partner-commission" type="number" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: Number(e.target.value) })} className="bg-[#0F0F0F] border-[#222] h-9" /></Field>
              </div>
              <DialogFooter>
                <Button data-testid="create-partner-submit" disabled={busy} onClick={create} className="bg-white text-black w-full">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kreye Patnè"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#0A0A0A] border-[#222] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.name} <span className="text-[#666] text-sm font-normal">@{detail.username}</span></DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-[#0F0F0F] border border-[#222] p-2"><div className="font-heading font-bold">{detail.customers}</div><div className="text-[10px] text-[#888]">Kliyan</div></div>
                  <div className="bg-[#0F0F0F] border border-[#222] p-2"><div className="font-heading font-bold text-[#22e07b]">{money(detail.revenue?.gross)}</div><div className="text-[10px] text-[#888]">Vant</div></div>
                  <div className="bg-[#0F0F0F] border border-[#222] p-2"><div className="font-heading font-bold">{detail.commission_pct}%</div><div className="text-[10px] text-[#888]">Komisyon</div></div>
                </div>

                {/* Full contact info (so the CEO can send login details) */}
                <div className="bg-[#0F0F0F] border border-[#222] p-3 space-y-1.5 font-mono-term text-xs" data-testid="partner-full-info">
                  <div className="flex justify-between gap-2"><span className="text-[#888]">Email</span><span className="truncate">{detail.email || "—"}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-[#888]">Username</span><span>@{detail.username}</span></div>
                  {detail.phone && <div className="flex justify-between gap-2"><span className="text-[#888]">Telefòn</span><span>{detail.phone}</span></div>}
                  {detail.company && <div className="flex justify-between gap-2"><span className="text-[#888]">Konpayi</span><span className="truncate">{detail.company}</span></div>}
                  {detail.platform && <div className="flex justify-between gap-2"><span className="text-[#888]">Platfòm</span><span>{detail.platform}{detail.followers ? ` · ${detail.followers}` : ""}</span></div>}
                  {detail.payout_email && <div className="flex justify-between gap-2"><span className="text-[#888]">PayPal</span><span className="truncate">{detail.payout_email}</span></div>}
                  {newPw ? (
                    <div className="mt-2 pt-2 border-t border-[#222] flex items-center justify-between gap-2 text-[#22e07b]">
                      <span>Nouvo modpas: <b className="text-white" data-testid="partner-new-password">{newPw.password}</b></span>
                      <button onClick={() => copy(newPw.password)} className="text-[#888] hover:text-white"><Copy className="h-3.5 w-3.5" /></button>
                    </div>
                  ) : (
                    <button onClick={() => resetPw(detail.id)} disabled={busy} data-testid="partner-reset-password"
                      className="mt-2 w-full h-8 rounded bg-[#1C8CFF]/15 text-[#8fc4ff] hover:bg-[#1C8CFF]/25 text-[11px] font-semibold">
                      Jenere Nouvo Modpas (pou re-voye enfo)
                    </button>
                  )}
                </div>

                {/* Referral links */}
                <div className="space-y-1.5">
                  {[["Lyen", detail.referral?.path_link]].map(([lbl, link]) => (
                    <div key={lbl} className="flex items-center gap-2 bg-[#0F0F0F] border border-[#222] px-2 py-1.5 rounded">
                      <span className="font-mono-term text-[9px] uppercase text-[#888] w-16 shrink-0">{lbl}</span>
                      <span className="text-xs truncate flex-1">{link}</span>
                      <button onClick={() => copy(link)} className="text-[#888] hover:text-white"><Copy className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>

                {/* Contract */}
                <div className="bg-[#0F0F0F] border border-[#222] p-3">
                  <div className="flex items-center gap-1.5 mb-2 text-[#888]"><FileText className="h-3.5 w-3.5" /><span className="font-mono-term text-[10px] uppercase tracking-widest">Kontra</span></div>
                  <div className="text-xs space-y-1">
                    <div className="flex justify-between"><span className="text-[#888]">Tip</span><span>{detail.contract?.label}</span></div>
                    <div className="flex justify-between"><span className="text-[#888]">Estati</span><span className={detail.contract?.status === "active" ? "text-[#22e07b]" : "text-[#ff5b5b]"}>{detail.contract?.status}</span></div>
                    <div className="flex justify-between"><span className="text-[#888]">Rete</span><span>{detail.contract?.remaining_days ?? "—"} jou</span></div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <ContractAction id={detail.id} onDone={() => openDetail(detail.id)} />
                  </div>
                </div>

                {/* Violations */}
                <ViolationsBlock partner={detail} onDone={() => openDetail(detail.id)} />

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-[#222]">
                  {detail.status !== "active"
                    ? <Button size="sm" disabled={busy} onClick={() => act(detail.id, "activate", "Aktive")} className="bg-[#22e07b]/15 text-[#22e07b] hover:bg-[#22e07b]/25 h-8"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Aktive</Button>
                    : <Button size="sm" disabled={busy} onClick={() => act(detail.id, "deactivate", "Dezaktive")} className="bg-[#444]/40 text-[#ccc] hover:bg-[#444]/60 h-8"><Ban className="h-3.5 w-3.5 mr-1" />Dezaktive</Button>}
                  <Button size="sm" onClick={() => del(detail.id)} className="bg-[#ff5b5b]/15 text-[#ff5b5b] hover:bg-[#ff5b5b]/25 h-8"><Trash2 className="h-3.5 w-3.5 mr-1" />Efase</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContractAction({ id, onDone }) {
  const [type, setType] = useState("1_month");
  const [busy, setBusy] = useState(false);
  const run = async (payload, label) => {
    setBusy(true);
    try { await api.post(`/admin/partners/${id}/contract`, payload); toast.success(label); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <select value={type} onChange={(e) => setType(e.target.value)} className="bg-[#0F0F0F] border border-[#222] h-8 rounded px-2 text-xs">
        <option value="1_month">1 Mwa</option><option value="2_months">2 Mwa</option><option value="3_months">3 Mwa</option>
        <option value="6_months">6 Mwa</option><option value="1_year">1 Ane</option>
      </select>
      <Button size="sm" disabled={busy} data-testid="renew-contract" onClick={() => run({ contract_type: type, status: "active" }, "Kontra renouvle")} className="bg-white text-black h-8 text-xs">Renouvle / Pwolonje</Button>
      <Button size="sm" disabled={busy} onClick={() => run({ contract_type: "", status: "suspended" }, "Sispann")} className="bg-[#f5a623]/15 text-[#f5a623] hover:bg-[#f5a623]/25 h-8 text-xs">Sispann</Button>
      <Button size="sm" disabled={busy} onClick={() => run({ contract_type: "", status: "terminated" }, "Fini")} className="bg-[#ff5b5b]/15 text-[#ff5b5b] hover:bg-[#ff5b5b]/25 h-8 text-xs">Termine</Button>
    </>
  );
}

function ViolationsBlock({ partner, onDone }) {
  const [type, setType] = useState("");
  const [desc, setDesc] = useState("");
  const [action, setAction] = useState("warning");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!type.trim() || !desc.trim()) { toast.error("Ranpli tip + deskripsyon"); return; }
    setBusy(true);
    try { await api.post(`/admin/partners/${partner.id}/violations`, { type, description: desc, action }); toast.success("Vyolasyon anrejistre"); setType(""); setDesc(""); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-[#0F0F0F] border border-[#222] p-3" data-testid="violations-block">
      <div className="flex items-center gap-1.5 mb-2 text-[#888]"><ShieldAlert className="h-3.5 w-3.5" /><span className="font-mono-term text-[10px] uppercase tracking-widest">Vyolasyon / Konfòmite</span></div>
      {partner.violations?.length > 0 ? (
        <div className="space-y-1.5 mb-3 max-h-32 overflow-y-auto">
          {partner.violations.map((v) => (
            <div key={v.id} className="text-xs border-b border-[#1a1a1a] pb-1">
              <div className="flex justify-between"><span className="font-semibold text-[#ff9b9b]">{v.type}</span><span className="text-[10px] uppercase text-[#888]">{v.action}</span></div>
              <div className="text-[#999]">{v.description}</div>
            </div>
          ))}
        </div>
      ) : <div className="text-xs text-[#666] mb-3">Pa gen vyolasyon.</div>}
      <div className="space-y-2">
        <Input data-testid="violation-type" value={type} onChange={(e) => setType(e.target.value)} placeholder="Tip vyolasyon" className="bg-[#0A0A0A] border-[#222] h-8 text-xs" />
        <Input data-testid="violation-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Deskripsyon" className="bg-[#0A0A0A] border-[#222] h-8 text-xs" />
        <div className="flex gap-2">
          <select value={action} onChange={(e) => setAction(e.target.value)} className="bg-[#0A0A0A] border border-[#222] h-8 rounded px-2 text-xs flex-1">
            <option value="warning">Avètisman</option><option value="note">Nòt</option>
            <option value="suspend">Sispann</option><option value="terminate">Termine</option>
          </select>
          <Button size="sm" disabled={busy} data-testid="add-violation" onClick={add} className="bg-white text-black h-8 text-xs">Anrejistre</Button>
        </div>
      </div>
    </div>
  );
}
